import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import { ChevronLeft } from '@lifeomic/chromicons';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles, useTheme } from '@lifeomic/chroma-react/styles';
import { getTestProps } from '../../util/testProps';
import { MemberExtractionReextract } from './MemberExtractionReextract';
import type { MemberExtractionReviewedHandler } from './MemberExtractionResultReview';
import { MemberExtractionResultRow } from './MemberExtractionResultRow';
import {
  MemberExtractionPdfPagesSkeleton,
  MemberExtractionSummarySkeleton,
} from './MemberExtractionSkeletons';
import {
  groupResourcesByType,
  resourceGroupColor,
  resourceGroupLabel,
  resourceTypeCounts,
} from './memberExtractionResourceGroups';
import {
  heroSectionLabelStyle,
  heroSpace,
  heroSurface,
} from './memberExtractionTheme';
import type {
  MemberExtractionRegionHover,
  MemberExtractionRegionPreview,
} from './MemberExtractionDocumentPages';
import type {
  MemberExtractionDocumentGroup,
  MemberExtractionRegion,
} from './useMemberExtractionDocuments';
import { useMemberExtractionSummary } from './useMemberExtractionSummary';

const messages = defineMessages({
  back: {
    id: 'phc-web-toolkit.memberExtractions.detail.back',
    defaultMessage: 'All documents',
  },
  results: {
    id: 'phc-web-toolkit.memberExtractions.detail.results',
    defaultMessage: 'Extracted results',
  },
  sourcePages: {
    id: 'phc-web-toolkit.memberExtractions.detail.sourcePages',
    defaultMessage: 'Source pages',
  },
});

const MemberExtractionDocumentPages = React.lazy(() =>
  import('./MemberExtractionDocumentPages').then(module => ({
    default: module.MemberExtractionDocumentPages,
  })),
);

const MemberExtractionDocumentSummary = React.lazy(() =>
  import('./MemberExtractionDocumentSummary').then(module => ({
    default: module.MemberExtractionDocumentSummary,
  })),
);

const useStyles = makeStyles(theme => ({
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 auto',
    width: 28,
    height: 28,
    borderRadius: '50%',
    backgroundColor: heroSurface.muted,
    color: theme.palette.text.primary,
    textDecoration: 'none',
    transition: `background-color ${heroSurface.transition}`,
    '&:hover': {
      backgroundColor: heroSurface.mutedHover,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  },
}));

const detailStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  height: '100%',
  minHeight: 0,
};

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: heroSpace.regular,
  padding: heroSpace.regular,
  borderBottom: `1px solid ${heroSurface.border}`,
};

const titleStyle = {
  flex: '0 1 auto',
  minWidth: 0,
  margin: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
};

const countsStyle = {
  flex: '0 0 auto',
  margin: 0,
  whiteSpace: 'nowrap' as const,
};

const typeCountNumberStyle = {
  fontWeight: 700,
};

const columnsStyle = {
  display: 'flex',
  flex: '1 1 auto',
  minHeight: 0,
};

const columnStyle = {
  flex: '1 1 0',
  minWidth: 0,
  height: '100%',
};

const columnContentStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: heroSpace.regular,
  boxSizing: 'border-box' as const,
  padding: heroSpace.regular,
};

// The columns scroll separately, so each clips its cards' shadows at its own
// edge. The gap between them is split in half so both sides keep room for it.
const resultsBesideSourceContentStyle = {
  ...columnContentStyle,
  paddingRight: heroSpace.tight,
};

const sideColumnContentStyle = {
  ...columnContentStyle,
  paddingLeft: heroSpace.tight,
};

const groupCardStyle = {
  margin: 0,
  padding: heroSpace.tight,
  listStyle: 'none' as const,
  backgroundColor: heroSurface.background,
  borderRadius: heroSurface.radiusLarge,
  boxShadow: heroSurface.shadowSmall,
};

const groupHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: heroSpace.tight,
  padding: heroSpace.tight,
};

const groupDotStyle = {
  width: 10,
  height: 10,
  borderRadius: '50%',
};

const groupCountStyle = {
  marginLeft: 'auto',
  color: heroSurface.textMuted,
};

const groupRowsStyle = {
  margin: 0,
  padding: 0,
  listStyle: 'none' as const,
};

// Inset by the card radius so the label lines up with the card's straight edge.
const sectionLabelStyle = {
  ...heroSectionLabelStyle,
  paddingLeft: heroSurface.radiusLarge,
};

const pagesCardStyle = {
  padding: heroSpace.regular,
  borderRadius: heroSurface.radiusLarge,
  backgroundColor: heroSurface.muted,
};

type MemberExtractionDocumentDetailProps = {
  patientId: string;
  group: MemberExtractionDocumentGroup;
  basePath: string;
  onReviewed?: MemberExtractionReviewedHandler;
  reextracting?: boolean;
  onReextractStarted?: (taskId: string) => void;
  resultsRevision?: number;
};

export const MemberExtractionDocumentDetail: React.FC<
  MemberExtractionDocumentDetailProps
> = ({
  patientId,
  group,
  basePath,
  onReviewed,
  reextracting,
  onReextractStarted,
  resultsRevision,
}) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const classes = useStyles();
  const summary = useMemberExtractionSummary(
    group.summaryReference,
    resultsRevision,
  );
  const resourceGroups = React.useMemo(
    () => groupResourcesByType(group.resources),
    [group.resources],
  );
  const hasSidePanel = Boolean(group.pdf || group.summaryReference);
  const [regionHover, setRegionHover] =
    React.useState<MemberExtractionRegionHover>();
  const [regionPreview, setRegionPreview] =
    React.useState<MemberExtractionRegionPreview>();
  const showOnPage = React.useCallback(
    (regions: MemberExtractionRegion[]) =>
      setRegionPreview(current => ({
        key: (current?.key ?? 0) + 1,
        regions,
      })),
    [],
  );
  const canShowOnPage = Boolean(group.pdf);

  return (
    <div style={detailStyle} {...getTestProps('member-extraction-document')}>
      <header style={headerStyle}>
        <Link
          to={basePath}
          className={classes.backLink}
          aria-label={formatMessage(messages.back)}
          title={formatMessage(messages.back)}
        >
          <ChevronLeft aria-hidden width={16} height={16} />
        </Link>
        <Text
          size="body"
          weight="bold"
          title={group.title}
          style={{ ...titleStyle, color: theme.palette.primary.dark }}
        >
          {group.title}
        </Text>
        <Text size="subbody" style={countsStyle}>
          {resourceTypeCounts(group.resources, formatMessage).map(
            (part, index) => (
              <React.Fragment key={part.key}>
                {index > 0 ? ' · ' : null}
                <span style={{ ...typeCountNumberStyle, color: part.color }}>
                  {part.count}
                </span>
                {` ${part.label}`}
              </React.Fragment>
            ),
          )}
        </Text>
        <MemberExtractionReextract
          patientId={patientId}
          sourceReference={group.sourceReference}
          running={reextracting}
          onStarted={onReextractStarted}
        />
      </header>
      <div style={columnsStyle}>
        <SimpleBar
          style={columnStyle}
          {...getTestProps('member-extraction-results')}
        >
          <div
            style={
              hasSidePanel
                ? resultsBesideSourceContentStyle
                : columnContentStyle
            }
          >
            <Text size="caption" style={sectionLabelStyle}>
              {formatMessage(messages.results)}
            </Text>
            {resourceGroups.map(resourceGroup => {
              const color = resourceGroupColor(resourceGroup.key);
              const label = resourceGroupLabel(
                resourceGroup.key,
                formatMessage,
              );
              return (
                <section
                  key={resourceGroup.key}
                  aria-label={label}
                  style={groupCardStyle}
                >
                  <div style={groupHeaderStyle}>
                    <span
                      aria-hidden
                      style={{ ...groupDotStyle, backgroundColor: color }}
                    />
                    <Text size="subbody" weight="bold" style={{ margin: 0 }}>
                      {label}
                    </Text>
                    <Text size="caption" style={groupCountStyle}>
                      {resourceGroup.resources.length}
                    </Text>
                  </div>
                  <ul style={groupRowsStyle}>
                    {resourceGroup.resources.map(resource => (
                      <MemberExtractionResultRow
                        key={`${resource.resourceType}/${resource.id}`}
                        patientId={patientId}
                        sourceReference={group.sourceReference}
                        resource={resource}
                        onReviewed={onReviewed}
                        onHoverRegions={
                          canShowOnPage ? setRegionHover : undefined
                        }
                        onShowOnPage={canShowOnPage ? showOnPage : undefined}
                      />
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
        </SimpleBar>
        {hasSidePanel ? (
          <SimpleBar
            style={columnStyle}
            {...getTestProps('member-extraction-source')}
          >
            <div style={sideColumnContentStyle}>
              {group.pdf ? (
                <>
                  <Text size="caption" style={sectionLabelStyle}>
                    {formatMessage(messages.sourcePages)}
                  </Text>
                  <div style={pagesCardStyle}>
                    <React.Suspense
                      fallback={<MemberExtractionPdfPagesSkeleton />}
                    >
                      <MemberExtractionDocumentPages
                        pdf={group.pdf}
                        regionHover={regionHover}
                        regionPreview={regionPreview}
                      />
                    </React.Suspense>
                  </div>
                </>
              ) : null}
              {group.summaryReference ? (
                <React.Suspense fallback={<MemberExtractionSummarySkeleton />}>
                  <MemberExtractionDocumentSummary
                    markdown={summary.markdown}
                    isLoading={summary.isLoading}
                    failed={summary.failed}
                  />
                </React.Suspense>
              ) : null}
            </div>
          </SimpleBar>
        ) : null}
      </div>
    </div>
  );
};
